import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Space,
  Button,
  DatePicker,
  Divider,
  Modal,
  Input,
  Select,
  Steps,
  message,
  Alert,
  Spin,
  Collapse,
  Card,
} from "antd";
import { isLogged, getAuthToken } from "../../../helpers/account";
import { api } from "../../../config/api";
import { UserOutlined } from "@ant-design/icons";
import { isEmailValid } from "../../../helpers/validators";
import { useDispatch } from "react-redux";
import NewBankForm from "../../../components/Dashboard/Account/Bank/Forms/NewBankForm";
import { useSelector } from "react-redux";
import { deliveryTypes } from "../../../data/delivery_types";
import { agreementTypes } from "../../../data/agreement_types";

const { TextArea } = Input;

const NewTransactionForm = ({
  email = "",
  title = "",
  initiatorTransactionRoleId = "",
  isForVirtualItem = false,
}) => {
  const { t } = useTranslation();
  const [newTransactionModalOpen, setNewTransactionModalOpen] = useState(false);
  const [isCounterpartyPickedUp, setIsCounterpartyPickedUp] = useState(false);
  const [isCounterpartyFound, setIsCounterpartyFound] = useState(true);
  const [invitedEmail, setInvitedEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [counterpartyUuid, setCounterpartyUuid] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const dispatch = useDispatch();
  const bankAccountsListUpdated = useSelector(
    (state) => state.bankAccountsListUpdated
  );

  const [formData, setFormData] = useState({
    title: "",
    agreementText: t('transaction.form.defaultAgreement'),
    initiator_transaction_role_id: 2,
    initiator_bank_id: undefined,
    counterparty_account_id: undefined,
    deliveryCost: "0.00",
    deliveryMethod: 13,
    returnMethod: 13,
    shipping_fee_paid_by_role_id: 2,
    escrow_fee_paid_by_role_id: 2,
    deliveryDate: null,
    inspectionDuration: undefined,
    discountCode: "",
  });

  const transactionRoles = [
    {
      value: 2,
      label: t('transaction.form.buyer'),
    },
    {
      value: 1,
      label: t('transaction.form.seller'),
    },
  ];

  useEffect(() => {
    if (email && isCounterpartyPickedUp === false) {
      handleFindCounterpartyByEmail(email);
    }
    if (isCounterpartyPickedUp && initiatorTransactionRoleId) {
      setFormData({
        ...formData,
        title: title,
        initiator_transaction_role_id: initiatorTransactionRoleId.value,
      });
    }
  }, [newTransactionModalOpen, isCounterpartyPickedUp]);

  useEffect(() => {
    const apiUrl = `${api.host}/bank`;

    if (newTransactionModalOpen && isLogged()) {
      // Fetch bank account data from the API
      fetch(apiUrl, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `${getAuthToken()}`,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          // Map the data to an array of objects with 'value' and 'label' properties
          const mappedBankAccounts = data.map((bank) => ({
            value: bank.id,
            label: bank.name,
          }));

          // Update the 'bankAccounts' state with the mapped data
          setBankAccounts(mappedBankAccounts);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching bank account data:", error);
          setLoading(false);
        });
    }
  }, [
    newTransactionModalOpen,
    isLogged,
    getAuthToken,
    bankAccountsListUpdated,
  ]);

  useEffect(() => {
    const text = formData.agreementText;
    const regex = /\[(.*?)\]/g; // Regular expression to match placeholders in square brackets.

    let matchedPlaceholders = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matchedPlaceholders.push(match[1]);
    }

    setPlaceholders(matchedPlaceholders);
  }, [formData.agreementText]);

  const handleFindCounterpartyByEmail = (value) => {
    value = value.toLowerCase();
    setFormData({ ...formData, counterpartyEmail: value });
    if (isEmailValid(value)) {
      if (newTransactionModalOpen && isLogged()) {
        const apiUrl = `${api.host}/accounts/find-by-email`;
        setLoading(true);
        fetch(apiUrl, {
          method: "POST",
          body: JSON.stringify({ email: value }),
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `${getAuthToken()}`,
          }),
        })
          .then((response) => {
            if (response.status === 404) {
              message.error(t('transaction.form.userNotFound', { email: value }));
              setIsCounterpartyPickedUp(false);
              setIsCounterpartyFound(false);
              setLoading(false);
            } else {
              return response.json(); // Continue with parsing response for non-404 status
            }
          })
          .then((data) => {
            setCounterpartyUuid(data.uuid); // Use "data" here, not "response"
            message.success(t('transaction.form.userFound'));
            setIsCounterpartyPickedUp(true);
            setIsCounterpartyFound(true);
            setLoading(false);
          })
          .catch((error) => {
            // Handle other errors here
            setIsCounterpartyPickedUp(false);
            setIsCounterpartyFound(false);
            setLoading(false);
          });
      }
    } else {
      setIsCounterpartyPickedUp(false);
    }
  };

  const handleCreateTransaction = () => {
    if (validateFormData()) {
      setLoading(true);
      const apiData = {
        initiator_account_uuid: sessionStorage.getItem("uuid"),
        initiator_bank_id: formData.initiator_bank_id,
        counterparty_account_uuid: counterpartyUuid,
        initiator_transaction_role_id: formData.initiator_transaction_role_id,
        shipping_method_id: formData.deliveryMethod,
        shipping_method_fee: formData.deliveryCost,
        shipping_fee_paid_by_account_uuid:
          formData.initiator_transaction_role_id ==
          formData.shipping_fee_paid_by_role_id
            ? sessionStorage.getItem("uuid")
            : counterpartyUuid,
        inspection_items_refused_shipping_method_id: formData.returnMethod,
        agreement: formData.agreementText,
        title: formData.title,
        inspection_period: formData.inspectionDuration,
        escrow_fee_paid_by_account_uuid:
          formData.initiator_transaction_role_id ==
          formData.escrow_fee_paid_by_role_id
            ? sessionStorage.getItem("uuid")
            : counterpartyUuid,
        agreement_items_delivery_at: formData.deliveryDate,
        discount_code: formData.discountCode,
      };

      const apiUrl = `${api.host}/transactions`;

      if (isLogged()) {
        // Fetch data from the API
        fetch(apiUrl, {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `${getAuthToken()}`, // Replace with your authorization method
          }),
          body: JSON.stringify(apiData), // Convert the object to JSON and send in the request body
        })
          .then((response) => response.json())
          .then((data) => {
            // Handle the response data as needed
            message.success(t('transaction.form.transactionCreated'));
            dispatch({
              type: "transactionDataUpdated",
              payload: {
                transactionDataChanged: new Date().getSeconds(),
              },
            });
            dispatch({
              type: "transactionCreated",
              payload: {
                transactionCreated: true,
              },
            });
            setNewTransactionModalOpen(false);
            setLoading(false);
            setTimeout(() => {
              window.location.href = `/transaction/${data.uuid}`;
            }, 2000);
          })
          .catch((error) => {
            setLoading(false);
            console.error("Error creating transaction:", error);
          });
      }
    }
  };

  const handleSendInvitation = () => {
    setLoading(true);
    const apiData = {
      email: invitedEmail,
    };

    const apiUrl = `${api.host}/accounts/send-invitation`;
    if (isLogged()) {
      // Fetch data from the API
      fetch(apiUrl, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `${getAuthToken()}`, // Replace with your authorization method
        }),
        body: JSON.stringify(apiData), // Convert the object to JSON and send in the request body
      })
        .then((response) => response.json())
        .then((data) => {
          // Handle the response data as needed
          message.success(t('transaction.form.invitationSent'));
          handleFindCounterpartyByEmail(invitedEmail);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          console.error("Error while sending transaction:", error);
        });
    }
  };

  const validateFormData = () => {
    // Walidacja danych
    console.log(formData);
    if (
      !formData.title ||
      !formData.agreementText ||
      !formData.initiator_transaction_role_id ||
      !formData.deliveryCost ||
      !formData.deliveryMethod ||
      !formData.returnMethod ||
      !formData.shipping_fee_paid_by_role_id ||
      !formData.escrow_fee_paid_by_role_id ||
      !formData.deliveryDate ||
      !formData.inspectionDuration
    ) {
      message.error(t('transaction.form.allFieldsRequired'));
      return false;
    }
    return true;
  };

  const handleNewTransaction = () => {
    setNewTransactionModalOpen(true);
  };

  const handleCancelNewTransaction = () => {
    setNewTransactionModalOpen(false);
  };

  const handleChangeAgreement = (value) => {
    Modal.confirm({
      title: t('transaction.form.updateAgreementConfirm'),
      content: t('transaction.form.updateAgreementWarning'),
      cancelText: t('common.cancel'),
      onOk() {
        setFormData({
          ...formData,
          agreementText: value,
        });
      },
    });
  };

  return (
    <>
      <Button 
        onClick={handleNewTransaction} 
        type="primary" 
        size="large"
        style={{
          width: "100%", 
          maxWidth: "400px",
          height: "56px",
          fontSize: "18px",
          fontWeight: "600"
        }}
      >
        📄 {t('transaction.form.newContract')}
      </Button>

      <Modal
        title={t('transaction.form.newContract')}
        open={newTransactionModalOpen} // Zmieniłem "open" na "visible"
        onOk={handleCreateTransaction}
        onCancel={handleCancelNewTransaction}
        okText={t('transaction.form.createContract')}
        cancelText={t('common.cancel')}
        styles={{
          body: { padding: "32px" },
          header: { borderBottom: "1px solid #f0f0f0", paddingBottom: "16px" }
        }}
        okButtonProps={{ disabled: !isCounterpartyPickedUp }}
        width={1000}
      >
        <Spin spinning={loading}>
          <Space size={15} direction="vertical" style={{ width: "100%" }}>
            <Steps
              size="small"
              current={0}
              items={[
                {
                  title: t('transaction.form.contractConditions'),
                },
                {
                  title: t('transaction.form.addingTasks'),
                },
              ]}
            />
            <Divider></Divider>

            <Space direction="vertical" style={{ width: "100%" }}>
              {!email ? (
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t('transaction.form.contractorEmail')}
                  onChange={(e) => {
                    handleFindCounterpartyByEmail(e.target.value);
                    setInvitedEmail(e.target.value);
                  }}
                  value={formData.counterpartyEmail}
                />
              ) : (
                <Alert
                  message={t('transaction.form.contractCreationInfo')}
                ></Alert>
              )}

              {isCounterpartyFound === false ? (
                <>
                  <Button type="primary" onClick={handleSendInvitation}>
                    {t('transaction.form.inviteToContinue')}
                  </Button>
                </>
              ) : (
                <></>
              )}
            </Space>

            {isCounterpartyPickedUp ? (
              <>
                <Collapse
                  defaultActiveKey={["1"]}
                  accordion
                  items={[
                    {
                      key: "1",
                      label: t('transaction.form.contractContentAndRole'),
                      children: (
                        <>
                          {" "}
                          <Space
                            size={30}
                            direction="vertical"
                            style={{ width: "100%" }}
                          >
                            <Input
                              placeholder={t('transaction.form.contractTitle')}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  title: e.target.value,
                                })
                              }
                              value={formData.title}
                              size="large"
                            />
                      
                            <TextArea
                              placeholder={t('transaction.form.contractText')}
                              rows={14}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  agreementText: e.target.value,
                                })
                              }
                              value={formData.agreementText}
                              size="large"
                            />
                            {placeholders.length > 0 ? (
                              <>
                                <Card>
                                  <Space direction="vertical">
                                    {t('transaction.form.fillTemplateData')}
                                    <ul>
                                      {placeholders.map(
                                        (placeholder, index) => (
                                          <li key={index}>{placeholder}</li>
                                        )
                                      )}
                                    </ul>
                                  </Space>
                                </Card>
                              </>
                            ) : (
                              <></>
                            )}

                            <Select
                              disabled
                              style={{
                                width: 150,
                              }}
                              placeholder={t('transaction.form.yourRole')}
                              options={transactionRoles}
                              onChange={(value) =>
                                setFormData({
                                  ...formData,
                                  initiator_transaction_role_id: value,
                                })
                              }
                              value={formData.initiator_transaction_role_id}
                            />
                          </Space>
                        </>
                      ),
                    },
                    {
                      key: "5",
                      label: t('transaction.form.deliveryAndInspection'),
                      children: (
                        <>
                          <Space size={30} direction="vertical">
                            <Space>
                              <DatePicker
                                onChange={(date) =>
                                  setFormData({
                                    ...formData,
                                    deliveryDate: date,
                                  })
                                }
                                value={formData.deliveryDate}
                              />

                              <Select
                                style={{
                                  width: 150,
                                }}
                                allowClear
                                placeholder={t('transaction.form.inspectionDuration')}
                                options={[
                                  {
                                    value: 1,
                                    label: t('transaction.form.oneDay'),
                                  },
                                  {
                                    value: 3,
                                    label: t('transaction.form.threeDays'),
                                  },
                                  {
                                    value: 7,
                                    label: t('transaction.form.sevenDays'),
                                  },
                                  {
                                    value: 14,
                                    label: t('transaction.form.fourteenDays'),
                                  },
                                  {
                                    value: 21,
                                    label: t('transaction.form.twentyOneDays'),
                                  },
                                ]}
                                onChange={(value) =>
                                  setFormData({
                                    ...formData,
                                    inspectionDuration: value,
                                  })
                                }
                                value={formData.inspectionDuration}
                              />
                            </Space>
                          </Space>
                        </>
                      ),
                    },
                  ]}
                />
              </>
            ) : (
              <>
                <Divider></Divider>
              </>
            )}
          </Space>
        </Spin>
      </Modal>
    </>
  );
};

export default NewTransactionForm;
